import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { bookPlace, checkBooking, unbookPlace } from 'src/api/bookings';
import { RoomPlaceShort } from 'src/api/places';
import {
  BackgroundData,
  DeskPlaceWithStatus,
  getPlanBackground,
  readDeskPlaceMicroCards,
  readPlaceAllBookings,
  readRoomPlaceInfos,
} from 'src/api/plan';
import { BookingObject } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';
import { toBookingObject } from 'src/utils/toBookingObject';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';
import { getEmployeeNumber } from '../user/selectors';

import { emptyDates } from './initialState';
import {
  deskBookingDurationHours,
  getCurrentDesk,
  getDeskLocation,
  getDesksState,
  getDesksTimePeriodUTC,
  getDesksTown,
} from './selectors';

export const desksPollingStarted = createAction<boolean, 'desks/started'>(
  'desks/started',
);
export const desksPollingStopped = createAction('desks/stopped');

export const desksExcludedDatesPollingStarted = createAction<
  boolean,
  'desks/excludedDatesStarted'
>('desks/excludedDatesStarted');
export const desksExcludedDatesPollingStopped = createAction(
  'desks/excludedDatesStopped',
);

export interface DesksAllData {
  backgroundData: BackgroundData | undefined;
  bookingObjects: Map<string, BookingObject>;
  staticObjects: Map<string, BookingObject>;
}

export const fetchDesks = createAsyncThunk<
  DesksAllData,
  boolean,
  ThunkConfig<string>
>(
  'desks/fetchDesks',
  async (withBackground, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const { floor } = getDeskLocation(state);
      const { from, to } = getDesksTimePeriodUTC(state);

      const data: DesksAllData = {
        backgroundData: undefined,
        bookingObjects: new Map(),
        staticObjects: new Map(),
      };

      const floorId = floor?.floorId;

      if (!floorId || !from || !to) return data;

      const promises: [
        Promise<DeskPlaceWithStatus[]>,
        Promise<DesksAllData['backgroundData']>,
        Promise<RoomPlaceShort[]>,
      ] = [
        readDeskPlaceMicroCards({ floorId, searchFrom: from, searchTo: to }),
        withBackground
          ? getPlanBackground(floorId)
          : Promise.resolve(undefined),
        readRoomPlaceInfos({ floorId, searchFrom: from, searchTo: to }),
      ];

      const [desks, backgroundData, rooms] = await Promise.all(promises);

      data.backgroundData = backgroundData;
      for (const desk of desks) {
        const bookingObject = toBookingObject(desk);
        data.bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
      }
      for (const room of rooms) {
        const bookingObject = toBookingObject(room);
        data.staticObjects.set(bookingObject.mapPlaceId, bookingObject);
      }

      return data;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as StoreState;
      const { floor } = getDeskLocation(state);
      const { from, to } = getDesksTimePeriodUTC(state);

      if (!floor?.floorId || !from || !to) return false;
    },
  },
);

export const fetchExcludedDates = createAsyncThunk<
  Set<string>,
  void,
  ThunkConfig<string>
>(
  'desks/fetchExcludedDates',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const desk = getCurrentDesk(state);
      const town = getDesksTown(state);
      if (!desk?.id) return emptyDates;

      const bookings = await readPlaceAllBookings(desk.id);
      const nextExcludedDates = new Set<string>();
      for (const booking of bookings) {
        const excludedDate = getDateTime({
          date: new Date(booking.bookedFrom),
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
          timezoneOffset: town?.timeZone,
        }).toISOString();
        if (excludedDate) nextExcludedDates.add(excludedDate);
      }

      return nextExcludedDates;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as StoreState;
      const { deskId, bookingObjects } = getDesksState(state);

      if (!deskId) return false;

      const desk = bookingObjects.get(deskId);
      if (desk?.status) return false;
    },
  },
);

export interface BookDeskParams {
  dates: Set<string>;
  bookedFor: string | undefined;
}

export const bookDesk = createAsyncThunk<
  void,
  BookDeskParams,
  ThunkConfig<string>
>(
  'desks/book',
  async ({ dates, bookedFor }, { getState, dispatch, rejectWithValue }) => {
    const bookOne = async ({
      id,
      from,
      to,
      responsible,
    }: {
      id: number;
      from: string;
      to: string;
      responsible: string;
    }): Promise<void> => {
      const bookPlaceResponse = await bookPlace({
        placeId: id,
        dateFrom: from,
        dateTo: to,
        responsible,
      });

      if (!bookPlaceResponse.accepted)
        throw new Error(bookPlaceResponse.rejectedReason ?? defaultServerError);

      await checkBooking({
        commandId: bookPlaceResponse.commandId,
        maxDurationExceededError:
          'Заявка на бронь отправлена, подтверждение придёт на email',
      });
    };

    try {
      const state = getState() as StoreState;

      const desk = getCurrentDesk(state);
      const town = getDesksTown(state);
      const employeeNumber = getEmployeeNumber(state);
      const responsible = bookedFor ?? employeeNumber;

      if (!desk?.id || !dates.size || !responsible) return;

      const requests: Promise<void>[] = [];

      for (const selectedDate of dates) {
        const fromDate = getDateTime({
          date: new Date(selectedDate),
          hours: 7,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
          timezoneOffset: town?.timeZone,
          toUTC: true,
        });
        const toDate = new Date(fromDate);
        toDate.setHours(toDate.getHours() + deskBookingDurationHours);

        const dateFrom = fromDate.toISOString();
        const dateTo = toDate.toISOString();

        if (!dateFrom || !dateTo) return;

        requests.push(
          bookOne({ id: desk.id, from: dateFrom, to: dateTo, responsible }),
        );
      }

      const responses = await Promise.allSettled(requests);

      for (const response of responses) {
        if (response.status === 'rejected') {
          const axiosError = response.reason as AxiosError;
          const data = axiosError.response?.data;
          const message = typeof data === 'string' ? data : axiosError.message;

          throw new Error(message);
        }
      }

      await dispatch(sidesheetActions.close());
      await dispatch(fetchDesks(false));
      await dispatch(
        messagesActions.show({
          message: `Рабочее место ${desk.title} успешно забронировано`,
        }),
      );
    } catch (e) {
      const message = (e as AxiosError).message;
      dispatch(messagesActions.show({ message: message }));
      return rejectWithValue(message);
    }
  },
);

export const unbookDesk = createAsyncThunk<void, void, ThunkConfig<string>>(
  'desks/unbook',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;

      const desk = getCurrentDesk(state);

      if (!desk?.status?.bookingId) return;

      const unbookPlaceResponse = await unbookPlace(desk.status.bookingId);

      if (!unbookPlaceResponse.accepted)
        throw new Error(
          unbookPlaceResponse.rejectedReason ?? defaultServerError,
        );

      const checkBookPlaceResponse = await checkBooking({
        commandId: unbookPlaceResponse.commandId,
        maxDurationExceededError:
          'Заявка на отмену брони отправлена, подтверждение придёт на email',
      });

      if (checkBookPlaceResponse.processed) {
        await dispatch(sidesheetActions.close());
        await dispatch(fetchDesks(false));
        await dispatch(
          messagesActions.show({
            message: `Бронь рабочего места ${desk.title} отменена`,
          }),
        );
      }
    } catch (e) {
      const message = (e as AxiosError).message;
      dispatch(messagesActions.show({ message }));
      return rejectWithValue(message);
    }
  },
);
